// src/useConversations.js
// Hook for conversation history — manages state + API calls to Supabase via Netlify functions

import { useState, useCallback } from "react";
import { supabase } from "./supabaseClient";

export function useConversations() {
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const getAccessToken = async () => {
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  };

  const authFetch = async (url, options = {}) => {
    const token = await getAccessToken();
    if (!token) throw new Error("Not authenticated");
    return fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
        ...options.headers,
      },
    });
  };

  // Load conversation list
  const loadConversations = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await authFetch("/api/conversations/");
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      }
    } catch (err) {
      console.error("Failed to load conversations:", err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Create a new conversation and return it
  const createConversation = useCallback(async (userId) => {
    try {
      const res = await authFetch("/api/conversations/", {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
      });
      if (res.ok) {
        const data = await res.json();
        const convo = data.conversation;
        setConversations((prev) => [convo, ...prev]);
        setActiveConversationId(convo.id);
        return convo;
      }
    } catch (err) {
      console.error("Failed to create conversation:", err);
    }
    return null;
  }, []);

  // Load messages for a specific conversation
  const loadMessages = useCallback(async (conversationId) => {
    try {
      const res = await authFetch(`/api/conversations/${conversationId}/messages`);
      if (res.ok) {
        const data = await res.json();
        return (data.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
        }));
      }
    } catch (err) {
      console.error("Failed to load messages:", err);
    }
    return [];
  }, []);

  // Save a single message to a conversation — returns true on success, false on failure
  const saveMessage = useCallback(async (conversationId, role, content) => {
    try {
      const res = await authFetch(`/api/conversations/${conversationId}/messages`, {
        method: "POST",
        body: JSON.stringify({ role, content }),
      });
      if (!res.ok) {
        console.error("Failed to save message: server returned", res.status);
        return false;
      }
      // Update the conversation's updated_at in local state so it sorts to top
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId
            ? { ...c, updated_at: new Date().toISOString() }
            : c
        ).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      );
      return true;
    } catch (err) {
      console.error("Failed to save message:", err);
      return false;
    }
  }, []);

  // Update conversation title in local state (auto-set by DB trigger, but
  // we also update locally for instant UI feedback)
  const updateLocalTitle = useCallback((conversationId, title) => {
    setConversations((prev) =>
      prev.map((c) =>
        c.id === conversationId && c.title === "New conversation"
          ? { ...c, title: title.slice(0, 80) }
          : c
      )
    );
  }, []);

  // Delete a conversation
  const deleteConversation = useCallback(async (conversationId) => {
    try {
      const res = await authFetch(`/api/conversations/${conversationId}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setConversations((prev) => prev.filter((c) => c.id !== conversationId));
        if (activeConversationId === conversationId) {
          setActiveConversationId(null);
        }
      }
    } catch (err) {
      console.error("Failed to delete conversation:", err);
    }
  }, [activeConversationId]);

  // Delete ALL conversations for the user — returns true on success, false on failure
  const deleteAllConversations = useCallback(async () => {
    try {
      // First, make sure we have the full list
      const res = await authFetch("/api/conversations/");
      let allConvos = conversations;
      if (res.ok) {
        const data = await res.json();
        allConvos = data.conversations || [];
      }

      if (allConvos.length === 0) return true;

      // Delete each conversation (RLS ensures only user's own)
      const results = await Promise.all(
        allConvos.map((c) =>
          authFetch(`/api/conversations/${c.id}`, { method: "DELETE" })
        )
      );
      if (results.some((r) => !r.ok)) {
        console.error("Some conversations failed to delete");
        return false;
      }
      setConversations([]);
      setActiveConversationId(null);
      return true;
    } catch (err) {
      console.error("Failed to delete all conversations:", err);
      return false;
    }
  }, [conversations]);

  return {
    conversations,
    activeConversationId,
    setActiveConversationId,
    loadingHistory,
    loadConversations,
    createConversation,
    loadMessages,
    saveMessage,
    updateLocalTitle,
    deleteConversation,
    deleteAllConversations,
  };
}
